import { useEffect, useMemo, useState, type FormEvent } from "react";
import qrcode from "qrcode-generator";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { useIdentity } from "../../auth/useIdentity";
import { isApplicantOnly } from "../../components/nav";
import { forgetTrustTokens } from "../../auth/trustedBrowser";
import { ChangePasswordCard } from "../operator/ChangePasswordCard";
import { ContactDetailsCard } from "./ContactDetailsCard";
import { PhotoCard } from "./PhotoCard";
import { SignInMethodsCard } from "./SignInMethodsCard";
import {
  accountErrorText,
  confirmTotpEnrolment,
  getAccountSecurity,
  groupKey,
  removeTotp,
  startTotpEnrolment,
  type TotpRegistration,
} from "../../api/account";

type State = "loading" | "off" | "setup" | "on" | "error";

/** The QR code as an image, drawn in the browser: the secret never leaves this page. */
function qrDataUrl(uri: string): string {
  const qr = qrcode(0, "M");
  qr.addData(uri);
  qr.make();
  return qr.createDataURL(5, 2);
}

/**
 * Settings › Sign-in & security: the signed-in person's own authenticator app.
 *
 * 🔑 Owner decisions 2026-09-15: Google Authenticator (any TOTP app) is
 * optional; once set up, AnkhOS asks for its code after every sign-in;
 * removing it needs a current code, and a lost phone is an administrator's
 * reset. radaccess acts on the token's subject only (api/account.ts).
 */
export function SecurityScreen() {
  const identity = useIdentity();
  // The account belongs to the token, not to a provider the console has
  // entered, so the call is contained by the token's own namespace.
  const namespace = identity.rootNamespace ?? identity.namespace ?? "";
  const [state, setState] = useState<State>("loading");
  const [loadError, setLoadError] = useState("");
  const [registration, setRegistration] = useState<TotpRegistration | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ severity: "success" | "error" | "info"; text: string } | null>(null);
  const qrSrc = useMemo(() => (registration ? qrDataUrl(registration.uri) : ""), [registration]);

  useEffect(() => {
    if (!namespace) return;
    const ac = new AbortController();
    getAccountSecurity(identity, namespace, ac.signal)
      .then((s) => setState(s.totpEnrolled ? "on" : "off"))
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        setLoadError(accountErrorText(err));
        setState("error");
      });
    return () => ac.abort();
    // identity is a new object on every render; the token and namespace are
    // what the answer depends on.
  }, [identity.token, namespace]);

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } catch (err) {
      setNotice({ severity: "error", text: accountErrorText(err) });
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  const start = () =>
    run(async () => {
      const reg = await startTotpEnrolment(identity, namespace);
      setRegistration(reg);
      setCode("");
      setState("setup");
    });

  const confirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await confirmTotpEnrolment(identity, namespace, code);
      setRegistration(null);
      setCode("");
      setState("on");
      forgetTrustTokens(); // radaccess ended every trusted browser too
      setNotice({ severity: "success", text: "Authenticator app set up. AnkhOS will ask for its code at your next sign-in, on every browser." });
    });
  };

  const remove = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await removeTotp(identity, namespace, code);
      setCode("");
      setState("off");
      forgetTrustTokens(); // radaccess ended every trusted browser too
      setNotice({ severity: "info", text: "Authenticator app removed. Your next sign-in asks for a code by SMS or email instead, on every browser." });
    });
  };

  const cancel = () => {
    setRegistration(null);
    setCode("");
    setNotice(null);
    setState("off");
  };

  const codeField = (label: string) => (
    <TextField
      label={label}
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
      autoComplete="one-time-code"
      size="small"
      slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "totp-code" } }}
      sx={{ maxWidth: 220 }}
    />
  );

  return (
    <Box sx={{ maxWidth: 720 }}>
      {isApplicantOnly(identity.roles) && (
        <Button component={RouterLink} to="/" variant="contained" sx={{ mb: 2 }} data-testid="continue-setup">
          Continue setup
        </Button>
      )}
      <Typography variant="overline" color="text.secondary">
        Settings
      </Typography>
      <Typography variant="h5" component="h1" gutterBottom>
        Sign-in &amp; security
      </Typography>
      {/* Owner 2026-10-23 could not find Change password: it is in Edit profile,
          but "password" in the menu search lands here, so it is here too. */}
      {/* Owner 2026-10-26: a photo users edit themselves (kept in radaccess). */}
      <PhotoCard />
      {/* Owner 2026-10-25: nowhere showed your own mobile and email. */}
      <ContactDetailsCard />
      {/* Owner 2026-10-25: link Google, forget trusted browsers. */}
      <SignInMethodsCard />
      <ChangePasswordCard />
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="h2">
              Authenticator app
            </Typography>
            {state === "on" && <Chip size="small" color="success" label="On" data-testid="totp-on" />}
            {state === "off" && <Chip size="small" label="Off" data-testid="totp-off" />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Use Google Authenticator or any authenticator app. Once it is set up, AnkhOS asks for the 6-digit code it
            shows every time you sign in, whichever way you sign in.
          </Typography>
          {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}

          {state === "loading" && <CircularProgress size={20} />}
          {state === "error" && <Alert severity="warning">Could not read your sign-in settings: {loadError}</Alert>}

          {state === "off" && (
            <Box>
              <Button variant="contained" onClick={() => void start()} disabled={busy}>
                Set up authenticator app
              </Button>
            </Box>
          )}

          {state === "setup" && registration && (
            <form onSubmit={confirm} noValidate>
              <Stack spacing={2}>
                <Typography variant="body2">1. Open the app and scan this code.</Typography>
                <Box component="img" src={qrSrc} alt="QR code to add AnkhOS to your authenticator app" data-testid="totp-qr" sx={{ width: 180, height: 180, imageRendering: "pixelated" }} />
                <Typography variant="body2">
                  Can't scan it? Enter this key instead:{" "}
                  <Box component="code" data-testid="totp-key" sx={{ fontFamily: "monospace", fontWeight: 600, userSelect: "all" }}>
                    {groupKey(registration.secret)}
                  </Box>
                </Typography>
                <Typography variant="body2">2. Enter the 6-digit code the app now shows.</Typography>
                {codeField("Code from the app")}
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" disabled={busy || code.length !== 6}>
                    {busy ? "Checking…" : "Confirm"}
                  </Button>
                  <Button onClick={cancel} disabled={busy}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </form>
          )}

          {state === "on" && (
            <form onSubmit={remove} noValidate>
              <Stack spacing={2}>
                <Typography variant="body2">
                  To remove it, enter the code the app shows now. Lost the phone? Ask your administrator to reset it.
                </Typography>
                {codeField("Current code")}
                <Box>
                  <Button type="submit" color="error" variant="outlined" disabled={busy || code.length !== 6}>
                    {busy ? "Removing…" : "Remove authenticator app"}
                  </Button>
                </Box>
              </Stack>
            </form>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
